import React from 'react';
import {get as _get} from 'lodash';
import {withRouter} from 'react-router-dom';

import {createForm} from '@utils/forms';
import {
    checkSubdivisionsAnyCountry,
    getDeviceNameById,
    getFieldNamesByDeviceId,
    prepareForHandleSwitchChanged, setCountryEntityGeoRestrictions,
    setSubdivisionsEntityGeoRestrictions,
} from '@modules/events/utils/eventsBookingGeoRestriction';
import {
    DEVICE_CATEGORY_DESKTOP,
    DEVICE_CATEGORY_MOBILE,
    DEVICE_CATEGORY_SMART_TV,
} from '@constants/variables';
import DefaultForm from '@appComponents/DefaultForm';
import {queryPrefillGeoRestrictionsForEventBooking, GetSelectedProduct} from '@graphql/eventEventBooking/query';
import {convertToInt} from '@utils/helpers';
import {onCloseModal} from '@modules/eventBooking/forms/helpers/helpers';
import {getAdditionalBackButton} from '@modules/events/utils/EventEventBooking/eventBookingDataFromAllSteps';
import {setCountriesOnChangeCallback} from '@utils/countryHelpers';

import EventBookingBlackoutZonesModel from './EventBookingBlackoutZonesModel';
import EventBookingBlackoutZonesForm from './EventBookingBlackoutZonesForm';

export class EventEventBookingGeoRestrictionsForm extends DefaultForm {
    countryTreeOptions = null;
    subdivisionTreeOptions = null;
    DEVICE_CATEGORIES = [DEVICE_CATEGORY_DESKTOP, DEVICE_CATEGORY_MOBILE, DEVICE_CATEGORY_SMART_TV];

    prepareDataForSubmit = (data) => {
        return {
            ...data,
            eventId: this.props.previousStepData.eventId,
            product: this.props.previousStepData.product,
            client: this.props.previousStepData.client,
            client_package: this.props.previousStepData.client_package ? this.props.previousStepData.client_package : null,
            auto_update_geo_restrictions: this.props.previousStepData.auto_update_geo_restrictions || false,
        };
    };

    onFormSubmit = (data) => {
        this.setState(() => ({
            defaultForm_errorShowed: false,
        }));

        const setMutationSuccessCallback = this.props.formParams.setMutationSuccessCallback;
        const Form = createForm(
                EventBookingBlackoutZonesModel,
                EventBookingBlackoutZonesForm,
                {
                    setMutationSuccessCallback,
                    id: data.id,
                    optionsVariables: {
                        event: this.props.formParams.optionsVariables.event,
                        isArchived: false,
                    },
                }),
            dataForNextStep = {
                ...data,
                ...this.props.previousStepData,
            };

        this.props.MessageBox.removeMessage(this.state.defaultForm_formValidationMessageBoxName);
        this.props.stepsMethods.setStep({
            id: 'step4',
            menuItem: {disabled: false},
            pane: {content: <Form previousStepData={dataForNextStep}/>},
        });
        this.props.stepsMethods.showStep('step4');
    };

    componentDidMount() {
        this.queryGetSelectedProduct();
    }

    queryGetSelectedProduct = () => {
        this.setState(() => ({queryGetSelectedProductLoading: true}));

        return this.runApolloRequest('query', {
            fetchPolicy: 'network-only',
            query: GetSelectedProduct,
            variables: {
                product: this.props.formParams.optionsVariables.product,
            },
        }).then((response) => {
            this.setState(() => ({
                selectedProduct: response.data,
            }));
        });
    };

    getPreFillGeoRestrictions = (eventId, clientPackageId, data) => {
        this.setState(() => ({defaultForm_loading: true}));

        return this.props.client.query({
            query: queryPrefillGeoRestrictionsForEventBooking,
            fetchPolicy: 'network-only',
            variables: {
                event: eventId,
                client_package: clientPackageId,
            },
        }).then((response) => {
            const preFillGeoRestrictions = response.data.prefillGeoRestrictionsForEventBooking;

            preFillGeoRestrictions.forEach((geoRestriction) => {
                setCountryEntityGeoRestrictions(convertToInt(geoRestriction.device_category), {
                    continents: data.continents,
                    geoRestrictions: geoRestriction.country_iso_alpha2_codes,
                }, this.setField);

                if (checkSubdivisionsAnyCountry(data.countries, geoRestriction.country_iso_alpha2_codes)) {
                    setSubdivisionsEntityGeoRestrictions(convertToInt(geoRestriction.device_category), {
                        countries: data.countries,
                        countriesGeoRestrictions: geoRestriction.country_iso_alpha2_codes,
                        subdivisionsGeoRestrictions: geoRestriction.country_subdivision_iso_codes || [],
                    }, this.setField);
                }
            });
        }).catch((response) => {
            this.props.MessageBox
                .addMessage('formInnerErrorMessage',
                    "Can't get pre-fill geo restrictions for selected distribution and client package.",
                    response.message, 'error');
        }).finally(
            () => {
                this.setState(() => ({defaultForm_loading: false}));
            }
        );
    };

    setupDeviceCategoriesCallbacks = () => {
        const {GraphQLOptionsData: {countries}, formValues} = this.props;

        if (!formValues || !countries) {
            return;
        }

        const devicesSwitchValues = {
            [DEVICE_CATEGORY_DESKTOP]: formValues.desktop_switch,
            [DEVICE_CATEGORY_MOBILE]: formValues.mobile_switch,
            [DEVICE_CATEGORY_SMART_TV]: formValues.smart_tv_switch,
        };

        this.DEVICE_CATEGORIES.forEach((deviceId) => {
            const {switchFieldName, dropdownCountriesFieldName, dropdownSubdivisionsFieldName} =
                getFieldNamesByDeviceId(deviceId);

            this.setOnChangeCallback({
                [switchFieldName]: (data) => {
                    this.handleSwitchChanged(deviceId, data.value);
                },
            });

            setCountriesOnChangeCallback(
                this.setField,
                () => this.props.formValues,
                countries,
                dropdownCountriesFieldName,
                dropdownSubdivisionsFieldName
            );
        });

        this.setField('geo_restrictions_error_content', {
            hidden: Object.values(devicesSwitchValues).every((value) => false !== value),
        });
    };

    componentDidUpdate(prevProps) {
        super.componentDidUpdate(prevProps);

        if (this.props !== prevProps) {
            this.props.stepsMethods.setForm('step3', this.props);
        }

        const {GraphQLOptionsData: {continents, countries}, formValues} = this.props;

        if (!this.state.deviceCategoriesGenerated) {
            this.setState(() => ({deviceCategoriesGenerated: true}));
        }

        if (formValues !== prevProps.formValues || countries !== prevProps.GraphQLOptionsData.countries) {
            this.setupDeviceCategoriesCallbacks();
        }

        if (!this.state.preFillGeoRestrictionsGenerated
            && !this.props.formParams.id
            && countries
            && continents
            && this.props.previousStepData?.eventId
            && this.props.previousStepData?.client_package) {

            this.getPreFillGeoRestrictions(
                convertToInt(this.props.previousStepData.eventId),
                convertToInt(this.props.previousStepData.client_package),
                {continents: continents, countries: countries}
            );

            this.setState(() => ({preFillGeoRestrictionsGenerated: true}));
        }

        const availableDeviceCategories = _get(this.state.selectedProduct, 'product.device_categories', []);

        if (this.props.GraphQLOptionsData !== prevProps.GraphQLOptionsData && this.state.selectedProduct) {
            availableDeviceCategories.map(deviceCategory => deviceCategory.id).forEach((deviceCategoryId) => {
                const deviceName = getDeviceNameById(parseInt(deviceCategoryId));

                this.setField(`${deviceName}_switch`, {
                    hidden: false,
                });
            });
        }
    }

    handleSwitchChanged = (deviceId, value) => {
        const result = prepareForHandleSwitchChanged({
            props: this.props,
            setField: this.setField,
            deviceId,
            value,
            initialCountryTreeOptions: this.countryTreeOptions,
            initialSubdivisionTreeOptions: this.subdivisionTreeOptions,
        });

        this.countryTreeOptions ??= result.countryTreeOptions;
        this.subdivisionTreeOptions ??= result.subdivisionTreeOptions;

        const deviceName = getDeviceNameById(deviceId);
        const switchFieldName = `${deviceName}_switch`;

        if (this.props.Model.editForm) {
            const step2FormData = this.props.stepsMethods.getForms()['step3'].formValues,
                combinedData = {...step2FormData, [switchFieldName]: !value};

            this.props.stepsMethods.setStep({
                id: 'step4',
                pane: {
                    props: {
                        timestamp: Date.now(),
                        switches: {
                            desktop_switch: combinedData.desktop_switch,
                            mobile_switch: combinedData.mobile_switch,
                            smart_tv_switch: combinedData.smart_tv_switch,
                        },
                    },
                },
            });
        }
    };

    renderAdditionalButtons = () => (
        getAdditionalBackButton(this.props.Model.editForm, this.props.stepsMethods.showPreviousStep)
    );

    renderDeleteButton(props) {
        return super.renderDeleteButton({
            ...props,
            content: 'Delete',
        });
    }

    onCancel = () => {
        onCloseModal(
            this.props.formParams.optionsVariables.event,
            this.props.formParams.optionsVariables.client,
            this.props.Modal.setModal,
            this.props
        );
    };

    renderCancelButton = (props) => super.renderCancelButton({
        ...props,
        type: 'button',
        onClick: this.onCancel,
    });

    renderSaveButton = (props) => {
        return super.renderSaveButton({
            ...props,
            content: 'Next',
            icon: 'angle right',
            labelPosition: 'right',
        });
    };
}

export default withRouter(EventEventBookingGeoRestrictionsForm);
